import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { fieldSx, GREEN_800 } from '../../core/pvtaStyles';
import { MAX_GUESTS_PER_RSVP } from '../registrationValidation';

interface EventGuestPickerProps {
    guestNames: string[];
    onChangeGuestCount: (nextCount: number) => void;
    onChangeGuestName: (guestIndex: number, name: string) => void;
    disabled?: boolean;
}

/**
 * Gaesteanzahl per Stepper plus optionale Namensfelder (Spec §2).
 * Ohne Namen zeigt die UI "Gast 1", "Gast 2" -- die Felder sind reine Komfortangaben.
 */
const EventGuestPicker: React.FC<EventGuestPickerProps> = ({
    guestNames,
    onChangeGuestCount,
    onChangeGuestName,
    disabled = false,
}) => {
    const guestCount = guestNames.length;

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: GREEN_800 }}>
                Gäste
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: guestCount ? 1.5 : 0 }}>
                <IconButton
                    aria-label="Einen Gast weniger"
                    onClick={() => onChangeGuestCount(Math.max(0, guestCount - 1))}
                    disabled={disabled || guestCount === 0}
                    sx={{ minHeight: 44, minWidth: 44, border: '1px solid', borderColor: 'divider' }}
                >
                    <RemoveRoundedIcon />
                </IconButton>
                <Typography
                    aria-live="polite"
                    variant="h6"
                    sx={{ minWidth: 32, textAlign: 'center', fontWeight: 700 }}
                >
                    {guestCount}
                </Typography>
                <IconButton
                    aria-label="Einen Gast mehr"
                    onClick={() => onChangeGuestCount(Math.min(MAX_GUESTS_PER_RSVP, guestCount + 1))}
                    disabled={disabled || guestCount >= MAX_GUESTS_PER_RSVP}
                    sx={{ minHeight: 44, minWidth: 44, border: '1px solid', borderColor: 'divider' }}
                >
                    <AddRoundedIcon />
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                    {guestCount === 1 ? 'Gast' : 'Gäste'}
                </Typography>
            </Stack>

            <Stack spacing={1.5}>
                {guestNames.map((name, guestIndex) => (
                    <TextField
                        key={`guest-${guestIndex}`}
                        label={`Name Gast ${guestIndex + 1} (optional)`}
                        value={name}
                        onChange={(event) => onChangeGuestName(guestIndex, event.target.value)}
                        disabled={disabled}
                        size="small"
                        fullWidth
                        sx={{ ...fieldSx }}
                    />
                ))}
            </Stack>
        </Box>
    );
};

export default EventGuestPicker;
